import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Button, Chip, Tabs, Tab, TextField, FormControl,
  InputLabel, Select, MenuItem, Badge, Collapse, Menu, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import { useNavigate } from 'react-router-dom';
import api, { defectStockService } from '../services/api';
import ManualWarehouseTransferDialog from '../components/ManualWarehouseTransferDialog';
import SendToCrusherDialog from '../components/SendToCrusherDialog';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';
import { orderRefFromRow } from '../utils/orderLabel';

const fmt = (v) => v ? new Date(v).toLocaleString('uk-UA') : '—';

export default function BrakWarehousePage() {
  const navigate = useNavigate();
  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
  }, []);
  const canSeeDefect = currentUser?.effective_permissions?.['menu.production.warehouse.defect'] === true;

  const [defects, setDefects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [sendingId, setSendingId] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [qtyFilter, setQtyFilter] = useState('all');
  const [sortKey, setSortKey] = useState('created');
  const [sortDir, setSortDir] = useState('desc');

  // Header buttons state
  const [transferHubOpen, setTransferHubOpen] = useState(false);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [infoDialog, setInfoDialog] = useState(null); // { title, text }
  const [crusherDialog, setCrusherDialog] = useState(null); // defect row

  const load = useCallback(async () => {
    if (!canSeeDefect) return;
    setLoading(true);
    try {
      const res = await api.get('/production-defect-stock/');
      setDefects(res.data?.results ?? res.data ?? []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [canSeeDefect]);

  useEffect(() => { load(); }, [load]);

  const handleSendToWaste = async ({ quantity, defect }) => {
    setSendingId(defect.id);
    try {
      await defectStockService.sendToWaste(defect.id, { quantity });
      setCrusherDialog(null);
      await load();
    } finally {
      setSendingId(null);
    }
  };

  const pending = useMemo(() => defects.filter((d) => d.status === 'pending'), [defects]);

  const filtered = useMemo(() => {
    let data = [...defects];
    const q = search.trim().toLowerCase();
    if (q) {
      data = data.filter((d) =>
        (d.product_name || '').toLowerCase().includes(q) ||
        (d.order_display || '').toLowerCase().includes(q)
      );
    }
    if (qtyFilter === 'gt0') data = data.filter((d) => d.quantity > 0);
    if (qtyFilter === 'eq0') data = data.filter((d) => d.quantity === 0);

    data.sort((a, b) => {
      let va, vb;
      if (sortKey === 'name') { va = a.product_name || ''; vb = b.product_name || ''; }
      else if (sortKey === 'quantity') { va = a.quantity; vb = b.quantity; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [defects, search, qtyFilter, sortKey, sortDir]);

  if (!canSeeDefect) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          Немає доступу до «Брак» (право menu.production.warehouse.defect).
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4">Брак</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant={shortageOpen ? 'contained' : 'outlined'} color="warning" startIcon={<WarningAmberIcon />}
              onClick={() => {
                if (!shortageOpen) {
                  setShortageLoading(true);
                  api.get('/material-shortages/').then(r => setShortages(r.data || [])).catch(() => setShortages([])).finally(() => setShortageLoading(false));
                }
                setShortageOpen(o => !o);
              }}>
              Нестача {shortages.length > 0 && !shortageOpen ? `(${shortages.length})` : ''}
            </Button>
            <Button variant="contained" color="primary" startIcon={<MenuBookIcon />} onClick={(e) => setRefsAnchorEl(e.currentTarget)}>
              Довідники
            </Button>
            <Menu anchorEl={refsAnchorEl} open={refsMenuOpen} onClose={() => setRefsAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
              {[
                ['/products', 'Вироби'],
                ['/raw-materials', 'Сировина'],
                ['/dyes', 'Фарби'],
                ['/packaging-materials', 'Упаковка'],
                ['/filament', 'Філамент'],
                ['/consumable-materials', 'Розхідні матеріали'],
              ].map(([path, label]) => (
                <MenuItem
                  key={path}
                  onClick={() => {
                    setRefsAnchorEl(null);
                    navigateWithReturn(navigate, path, stockReturnTo('defect'), stockReturnLabel('defect'));
                  }}
                >
                  {label}
                </MenuItem>
              ))}
            </Menu>
            <Button variant="contained" color="success" startIcon={<InputIcon />}
              onClick={() => setInfoDialog({ title: 'Прихід — Брак', text: 'Брак надходить на склад автоматично після фіксації у виробничому звіті. Ручне надходження не передбачено.' })}>
              Прихід
            </Button>
            <Button variant="contained" color="error" startIcon={<OutputIcon />}
              onClick={() => setInfoDialog({ title: 'Вибуття — Брак', text: 'Вибуття браку — кнопка «На дробарку» в рядку. Можна відправити частину залишку (шт); решта лишається на складі. Партія зʼявиться в черзі «Нові» на робочому місці Дробарка.' })}>
              Вибуття
            </Button>
            <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />} onClick={() => setTransferHubOpen(true)}>
              Переміщення
            </Button>
          </Box>
        </Box>
        <Collapse in={shortageOpen}>
          <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
                ⚠️ Нестача матеріалів для запланованих виробничих замовлень
              </Typography>
            </Box>
            {shortageLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
            ) : shortages.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="success.main" fontWeight={600}>✅ Нестач немає</Typography>
              </Box>
            ) : (
              <TableContainer><Table size="small">
                <TableHead><TableRow>
                  <TableCell>Матеріал</TableCell><TableCell align="right">Потрібно</TableCell>
                  <TableCell align="right">Є на складі</TableCell><TableCell align="right">Нестача</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {shortages.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell>{s.material_name || s.name}</TableCell>
                      <TableCell align="right">{s.required_quantity ?? '—'}</TableCell>
                      <TableCell align="right">{s.available_quantity ?? '—'}</TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{s.shortage ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></TableContainer>
            )}
          </Box>
        </Collapse>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            label="Пошук по виробу або замовленню"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: '1 1 280px' }}
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Фільтр</InputLabel>
            <Select label="Фільтр" value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
              <MenuItem value="all">Усі</MenuItem>
              <MenuItem value="gt0">Тільки &gt; 0</MenuItem>
              <MenuItem value="eq0">Тільки = 0</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Сортування</InputLabel>
            <Select label="Сортування" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
              <MenuItem value="created">За датою</MenuItem>
              <MenuItem value="name">За назвою</MenuItem>
              <MenuItem value="quantity">За кількістю</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Напрям</InputLabel>
            <Select label="Напрям" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
              <MenuItem value="asc">↑ Зростання</MenuItem>
              <MenuItem value="desc">↓ Спадання</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => {
            setSearch(''); setQtyFilter('all'); setSortKey('created'); setSortDir('desc');
          }}>Скинути</Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label={`Залишки (${filtered.length})`} />
            <Tab label={<Badge color="error" badgeContent={pending.length || 0}><Box sx={{ pr: pending.length ? 1 : 0 }}>Очікують відправки</Box></Badge>} />
          </Tabs>
        </Box>
      </Box>

      <Box>
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : (
          <>
            {tab === 0 && (
              <TableContainer component={Paper}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Замовлення</TableCell>
                      <TableCell>Виріб</TableCell>
                      <TableCell align="right">Кількість, шт</TableCell>
                      <TableCell align="right">Вага, кг</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Дата</TableCell>
                      <TableCell align="center">Дія</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary' }}>
                          Немає браку на складі
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.order_display || orderRefFromRow(d)}</TableCell>
                          <TableCell>{d.product_name || d.product}</TableCell>
                          <TableCell align="right">{d.quantity}</TableCell>
                          <TableCell align="right">{d.weight_kg ?? '—'}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={d.status === 'pending' ? 'warning' : 'default'}
                              label={d.status === 'pending' ? 'Очікує' : 'Відправлено'}
                            />
                          </TableCell>
                          <TableCell>{fmt(d.created_at)}</TableCell>
                          <TableCell align="center">
                            {d.status === 'pending' && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                startIcon={sendingId === d.id ? <CircularProgress size={14} /> : <DeleteOutlineIcon />}
                                disabled={sendingId === d.id}
                                onClick={() => setCrusherDialog(d)}
                              >
                                На дробарку
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {tab === 1 && (
              <TableContainer component={Paper}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Замовлення</TableCell>
                      <TableCell>Виріб</TableCell>
                      <TableCell align="right">Кількість, шт</TableCell>
                      <TableCell align="right">Вага, кг</TableCell>
                      <TableCell>Дата</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pending.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                          Немає позицій, що очікують відправки на відходи
                        </TableCell>
                      </TableRow>
                    ) : (
                      pending.map((d) => (
                        <TableRow key={d.id} hover>
                          <TableCell>{d.order_display || orderRefFromRow(d)}</TableCell>
                          <TableCell>{d.product_name || d.product}</TableCell>
                          <TableCell align="right">{d.quantity}</TableCell>
                          <TableCell align="right">{d.weight_kg ?? '—'}</TableCell>
                          <TableCell>{fmt(d.created_at)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Box>

      <Dialog open={Boolean(infoDialog)} onClose={() => setInfoDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{infoDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1 }}>{infoDialog?.text}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialog(null)}>Закрити</Button>
        </DialogActions>
      </Dialog>

      <SendToCrusherDialog
        open={Boolean(crusherDialog)}
        defect={crusherDialog}
        onClose={() => !sendingId && setCrusherDialog(null)}
        onSubmit={handleSendToWaste}
      />

      <ManualWarehouseTransferDialog
        hubOpen={transferHubOpen}
        onHubClose={() => setTransferHubOpen(false)}
        sourceWarehouseId="defect"
        skipHub
        formTitle="Ручне переміщення браку"
        hubHint="Перемістіть брак на будь-який склад системи. Рух зʼявиться в журналі; залишки оновляться одразу."
        options={pending.filter((d) => Number(d.quantity) > 0)}
        getOptionKey={(d) => String(d.id)}
        getOptionQty={(d) => Number(d.quantity) || 0}
        getOptionLabel={(d) => {
          const order = d.order_display || orderRefFromRow(d);
          return `${order}: ${d.product_name || '—'} — ${d.quantity} шт (${d.weight_kg ?? 0} кг)`;
        }}
        qtyLabel="Кількість, шт"
        onSubmit={async ({ option, quantity, destination, note }) => {
          await defectStockService.transfer(option.id, {
            quantity: Math.round(quantity),
            destination,
            note,
          });
          await load();
        }}
      />
    </Box>
  );
}
